import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { Box, Center, Text as MantineText } from "@mantine/core";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { useTranslation } from "react-i18next";

import { Button, DatePicker, Loader, Modal, TextInput } from "components";
import { HolidayService } from "services";
import { HolidayRequest, HolidayResponse } from "types";
import { DateUtils } from "utils";

import classes from "./HolidaysPage.module.css";

interface HolidayFormModalProps {
  holiday?: HolidayResponse;
  onClose: () => void;
  onSaved: (holiday: HolidayResponse) => Promise<void> | void;
  opened: boolean;
}

interface HolidayFormValues {
  date: Date | null;
  name: string;
}

const EMPTY_FORM_VALUES: HolidayFormValues = {
  date: null,
  name: "",
};

const parseLocalDate = (value: string) => {
  const [year, month, day] = value.split("-").map(Number);
  return new Date(year, month - 1, day);
};

const buildPayload = (values: HolidayFormValues): HolidayRequest => ({
  date: DateUtils.formatDateLocal(values.date!),
  name: values.name.trim(),
});

const HolidayFormModal = ({ holiday, onClose, onSaved, opened }: HolidayFormModalProps) => {
  const { t } = useTranslation();
  const isEditMode = Boolean(holiday);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const form = useForm<HolidayFormValues>({
    initialValues: EMPTY_FORM_VALUES,
    validate: {
      date: (value) => value ? null : t("error.required"),
      name: (value) => value.trim().length > 0 ? null : t("error.required"),
    },
  });
  const formRef = useRef(form);

  useEffect(() => {
    formRef.current = form;
  }, [form]);

  const modalTitle = useMemo(
    () =>
      isEditMode
        ? t("holidays.form.editTitle")
        : t("holidays.form.createTitle"),
    [isEditMode, t],
  );

  useEffect(() => {
    if (!opened) {
      formRef.current.reset();
      formRef.current.clearErrors();
      setIsSubmitting(false);
      return;
    }

    if (holiday) {
      formRef.current.setValues({
        date: parseLocalDate(holiday.date),
        name: holiday.name,
      });
    } else {
      formRef.current.setValues(EMPTY_FORM_VALUES);
    }

    formRef.current.clearErrors();
  }, [holiday, opened]);

  const handleSubmit = useCallback(async () => {
    const validation = form.validate();

    if (validation.hasErrors) {
      return;
    }

    const payload = buildPayload(form.getValues());
    setIsSubmitting(true);

    try {
      const savedHoliday = holiday
        ? await HolidayService.holidayUpdate(holiday.id, payload)
        : await HolidayService.holidayCreate(payload);

      notifications.show({
        color: "green",
        message: t(
          holiday
            ? "holidays.notifications.updateSuccessMessage"
            : "holidays.notifications.createSuccessMessage",
        ),
        title: t("holidays.notifications.saveSuccessTitle"),
      });

      await onSaved(savedHoliday);
      onClose();
    } catch {
      notifications.show({
        color: "red",
        message: t(
          holiday
            ? "holidays.notifications.updateErrorMessage"
            : "holidays.notifications.createErrorMessage",
        ),
        title: t("holidays.notifications.saveErrorTitle"),
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [form, holiday, onClose, onSaved, t]);

  return (
    <Modal opened={opened} onClose={onClose} title={modalTitle}>
      <Box pos="relative">
        <Loader overlay={true} visible={isSubmitting} />

        <MantineText className={classes.modalHint}>
          {t("holidays.form.hint")}
        </MantineText>

        <DatePicker
          clearable={false}
          label={t("holidays.form.fields.date")}
          mb="md"
          required={true}
          value={form.values.date}
          onChange={(value: Date | null) => form.setFieldValue("date", value)}
          error={form.errors.date}
        />

        <TextInput
          label={t("holidays.form.fields.name")}
          placeholder={t("holidays.form.fields.namePlaceholder")}
          required={true}
          {...form.getInputProps("name")}
        />

        <Center className={classes.modalActions}>
          <Button disabled={isSubmitting} loading={isSubmitting} onClick={handleSubmit}>
            {isEditMode ? t("action.save") : t("action.create")}
          </Button>
        </Center>
      </Box>
    </Modal>
  );
};

export default HolidayFormModal;
